import { createContextApi, emitPlatformEvent, invalidatesOne, providesOne, providesList, createCursorPagedEndpoint, registerCrossContextInvalidation } from '@quickbee/store';

// src/index.ts
var PAYMENT_TAGS = ["PaymentOrder", "Quote"];
var QUOTE_ID = "CURRENT";
var paymentEndpoints = (build) => ({
  /**
   * Prices a hypothetical payment. Creates nothing and reserves no rate — the price is only
   * locked when an order is actually created, so this response carries no expiry to honour.
   *
   * Cached for 15s all the same: the underlying gold rate moves, and a breakdown shown next
   * to a "Pay" button should not be minutes old. `pricing.updated` evicts it sooner (see
   * `PAYMENT_INVALIDATION_RULES`).
   */
  getQuote: build.query({
    // Spelled out rather than spread: these three names are the controller's
    // `@RequestParam`s, and `amount`/`purpose` guessed from the arg object is exactly
    // the kind of drift I3 exists to prevent. `undefined` values are dropped by the client.
    query: ({ amountFiat, purpose, channel }) => ({
      url: "/payments/quote",
      params: { amountFiat, purpose, channel }
    }),
    keepUnusedDataFor: 15,
    providesTags: [{ type: "Quote", id: QUOTE_ID }]
  }),
  /**
   * The payment book, cursor-paginated. Resolves to {@link PaymentOrderSummaryResponse}
   * grid rows — **not** the detail shape `getOrder` returns.
   *
   * Who the rows belong to depends on the caller's effective scope, not on this call:
   * an own-scoped caller always gets their own orders (a supplied `customerId` is
   * ignored, never a 403), a `payment:read:tenant` caller gets one customer's or the
   * whole tenant's. See {@link ListPaymentOrdersArgs}.
   *
   * A back-office screen reads `statusDetail`, not `status` — the latter is the
   * three-value badge the customer-facing clients render, in which
   * `AWAITING_VERIFICATION` is indistinguishable from `CREATED`.
   */
  listOrders: build.query({
    query: ({ size, cursor, sort, purpose, status, customerId }) => ({
      url: "/payments/orders",
      params: { size, cursor, sort, purpose, status, customerId }
    }),
    ...createCursorPagedEndpoint(),
    providesTags: (result) => providesList("PaymentOrder", result),
    keepUnusedDataFor: 120
  }),
  /**
   * The cash-verification queue (B3) — collections whose money has arrived and whose
   * gold has not, because approval is the allocating act.
   *
   * Requires `payment:read:tenant`; an `AGENT`, who may record a collection, cannot
   * read it. Cached briefly on purpose: this is a work queue two reviewers may hold
   * open at once, and a stale row is one they both try to approve.
   */
  listOfflineCollections: build.query({
    query: ({ size, cursor, status, channel, collectedByUserId }) => ({
      url: "/payments/offline-collections",
      params: { size, cursor, status, channel, collectedByUserId }
    }),
    ...createCursorPagedEndpoint(),
    providesTags: (result) => providesList("PaymentOrder", result),
    keepUnusedDataFor: 30
  }),
  getOrder: build.query({
    query: (id) => ({ url: `/payments/orders/${id}` }),
    providesTags: (_r, _e, id) => providesOne("PaymentOrder", id)
  }),
  /**
   * Returns the gateway order id and the **public** Razorpay `key_id` for Checkout.js — not
   * the order itself. Fetch the order with `getOrder` if the screen needs the full detail.
   */
  createOrder: build.mutation({
    query: ({ draftId, ...body }) => ({
      url: "/payments/orders",
      method: "POST",
      body,
      idempotencyKey: `payment-order:${body.purpose}:${draftId}`
    }),
    invalidatesTags: [{ type: "PaymentOrder", id: "LIST" }]
  }),
  /**
   * Optimistic checkout-callback verification (ADR-003). It **never** credits gold — that
   * authority belongs exclusively to the webhook capture path — so the order usually comes
   * back `AUTHORIZED`, and only reads `CAPTURED` when the webhook won the race. A screen
   * that must show a settled payment should poll `getOrder` rather than trust this status.
   *
   * The {@link CaptureContext} fields are client-side only and never sent — the request record
   * has exactly three fields.
   */
  verifyOrder: build.mutation({
    query: ({ id, schemeEnrollmentId: _enrollment, customerId: _customer, ...body }) => ({
      url: `/payments/orders/${id}/verify`,
      method: "POST",
      body,
      idempotencyKey: `payment-verify:${id}`
    }),
    invalidatesTags: (_r, _e, { id }) => invalidatesOne("PaymentOrder", id),
    async onQueryStarted({ schemeEnrollmentId, customerId }, { queryFulfilled }) {
      try {
        const { data } = await queryFulfilled;
        if (data.status === "CAPTURED") {
          emitPlatformEvent("payment.captured", {
            orderId: data.id,
            ...schemeEnrollmentId ? { enrollmentId: schemeEnrollmentId } : {},
            ...customerId ? { customerId } : {}
          });
        }
      } catch {
      }
    }
  }),
  /**
   * RM/tenant-admin only (`payment:refund:tenant`); there is no customer-facing refund in v1.
   *
   * `draftId` — not the amount — scopes the key, because the backend turns this key into the
   * refund's identity. Keying on the amount would make a second, genuinely intended refund of
   * the same amount silently resolve to the first one.
   */
  refundOrder: build.mutation({
    query: ({ id, draftId, ...body }) => ({
      url: `/payments/orders/${id}/refunds`,
      method: "POST",
      body,
      idempotencyKey: `payment-refund:${id}:${draftId}`
    }),
    invalidatesTags: (_r, _e, { id }) => invalidatesOne("PaymentOrder", id),
    async onQueryStarted({ id }, { queryFulfilled }) {
      try {
        const { data } = await queryFulfilled;
        emitPlatformEvent("payment.refunded", { orderId: id, refundId: data.id });
      } catch {
      }
    }
  }),
  /**
   * The *maker* half of the maker–checker control (`payment:collect:tenant`): records money
   * an agent took in the field or a customer wired direct. Creates an order in
   * `AWAITING_VERIFICATION` — no gold is allocated until a reviewer confirms receipt.
   */
  recordOfflineCollection: build.mutation({
    query: ({ draftId, ...body }) => ({
      url: "/payments/offline-collections",
      method: "POST",
      body,
      idempotencyKey: `offline-collection:${draftId}`
    }),
    invalidatesTags: [{ type: "PaymentOrder", id: "LIST" }]
  }),
  /**
   * The *checker* half (`payment:verify:tenant`, a deliberately separate grant). Approval is
   * what allocates gold — the order goes straight to `CAPTURED`, so this emits the same
   * cross-context event the gateway path does. Rejection lands on `VERIFICATION_REJECTED`.
   *
   * Keyed on the order alone: a reviewer decides once, and both outcomes are terminal.
   */
  verifyOfflineCollection: build.mutation({
    query: ({ id, schemeEnrollmentId: _enrollment, customerId: _customer, ...body }) => ({
      url: `/payments/offline-collections/${id}/verification`,
      method: "POST",
      body,
      idempotencyKey: `offline-collection-verify:${id}`
    }),
    invalidatesTags: (_r, _e, { id }) => invalidatesOne("PaymentOrder", id),
    async onQueryStarted({ schemeEnrollmentId, customerId }, { queryFulfilled }) {
      try {
        const { data } = await queryFulfilled;
        if (data.status === "CAPTURED") {
          emitPlatformEvent("payment.captured", {
            orderId: data.id,
            ...schemeEnrollmentId ? { enrollmentId: schemeEnrollmentId } : {},
            ...customerId ? { customerId } : {}
          });
        }
      } catch {
      }
    }
  })
});

// src/index.ts
var paymentApi = createContextApi({
  context: "payment",
  reducerPath: "paymentApi",
  tagTypes: PAYMENT_TAGS,
  mocks: () => import('./mocks-V4ZPVFNM.js').then((module) => module.paymentMocks),
  endpoints: paymentEndpoints
});
var PAYMENT_INVALIDATION_RULES = [
  {
    event: "pricing.updated",
    invalidate: () => [{ type: "Quote", id: QUOTE_ID }],
    settleDelayMs: 0
  }
];
function registerPaymentInvalidation(store) {
  return registerCrossContextInvalidation({
    store,
    api: paymentApi,
    rules: PAYMENT_INVALIDATION_RULES
  });
}
var {
  useGetQuoteQuery,
  useLazyGetQuoteQuery,
  useListOrdersQuery,
  useListOfflineCollectionsQuery,
  useGetOrderQuery,
  useLazyGetOrderQuery,
  useCreateOrderMutation,
  useVerifyOrderMutation,
  useRefundOrderMutation,
  useRecordOfflineCollectionMutation,
  useVerifyOfflineCollectionMutation
} = paymentApi;

export { PAYMENT_INVALIDATION_RULES, PAYMENT_TAGS, QUOTE_ID, paymentApi, registerPaymentInvalidation, useCreateOrderMutation, useGetOrderQuery, useGetQuoteQuery, useLazyGetOrderQuery, useLazyGetQuoteQuery, useListOfflineCollectionsQuery, useListOrdersQuery, useRecordOfflineCollectionMutation, useRefundOrderMutation, useVerifyOfflineCollectionMutation, useVerifyOrderMutation };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map